import { useCallback } from 'react';
import type { BlockGroup as Group } from '@/types/workflow';

export interface UseLayerHandlersDeps {
  groups: Group[];
  navigateInto: (id: string) => void;
  navigateToLevel: (depth: number) => void;
  resetLayerPath: () => void;
}

export function useLayerHandlers(deps: UseLayerHandlersDeps) {
  const { groups, navigateInto, navigateToLevel, resetLayerPath } = deps;

  /**
   * Jump to the layer that contains `groupId`. If null, return to the top level.
   * Walks up parentGroupId pointers to reconstruct the path.
   */
  const handleNavigateToLayer = useCallback((groupId: string | null) => {
    if (groupId === null) {
      resetLayerPath();
      return;
    }
    const path: string[] = [];
    let current = groupId;
    while (current) {
      path.unshift(current);
      const group = groups.find(g => g.id === current);
      current = group?.parentGroupId ?? '';
    }
    navigateToLevel(0);
    for (const id of path) navigateInto(id);
  }, [groups, navigateInto, navigateToLevel, resetLayerPath]);

  return { handleNavigateToLayer };
}
